import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { ROUTES, SCOPE } from '@/constants'
import storage from '@/utils/storage'
import tokens, { getJWTFromStorage, REQUIRED_JWT_STORAGE_KEYS } from '@/utils/tokens'
import config from '@/config'
import { toQS } from '@/utils/url'

export const handleRedirectToAuth = (targetRoute) => {
  tokens.clearAllTokenData()
  window.location.replace(`${config.api.auth}?${toQS({
    scope: SCOPE,
    callbackUrl: config.app.url.concat(ROUTES.auth),
    tokenVersion: 3,
    targetRoute,
  })}`)
}

const getQueryValue = value => (Array.isArray(value) ? value[0] : value)

const getTokenDataFromQuery = query => Object.entries({
  accountId: getQueryValue(query.accountId),
  oauthToken: getQueryValue(query.oauthToken),
  jwt: getQueryValue(query.jwt),
  jwtExpiresAt: getQueryValue(query.jwtExpiresAt),
  refreshJWT: getQueryValue(query.refreshJWT),
  refreshJWTExpiresAt: getQueryValue(query.refreshJWTExpiresAt),
}).reduce((data, [key, value]) => (
  value ? { ...data, [key]: value } : data
), {})

export default function useAuth() {
  const router = useRouter()
  const [isAuthorized, setIsAuthorized] = useState(!!getJWTFromStorage())
  const [isAuthReady, setIsAuthReady] = useState(false)

  useEffect(() => {
    if (!router.isReady) {
      setIsAuthReady(false)
      return
    }

    async function maybeFetchNewTokens() {
      const tokenData = getTokenDataFromQuery(router.query)
      const targetRoute = getQueryValue(router.query.targetRoute) || ROUTES.overview
      try {
        if (router.query.fromJawn === 'true') {
          await tokens.fetchJWT({ accountId: tokenData.accountId })
        }
        setIsAuthorized(!!getJWTFromStorage())
        router.replace(targetRoute)
      } catch (err) {
        handleRedirectToAuth(targetRoute)
      }
    }
    if (router.route === ROUTES.auth) {
      setIsAuthReady(false)
      tokens.setTokenData(getTokenDataFromQuery(router.query))
      maybeFetchNewTokens()
    } else if (REQUIRED_JWT_STORAGE_KEYS.some(key => !storage.get(key))) {
      setIsAuthReady(false)
      handleRedirectToAuth(router.route)
    } else {
      setIsAuthReady(true)
    }
  }, [router.isReady, router.query, router.route])

  useEffect(() => {
    const onSyncCb = () => setIsAuthorized(!!getJWTFromStorage())
    const onErrorCb = () => handleRedirectToAuth()
    tokens.onSync(onSyncCb)
    tokens.onError(onErrorCb)
    return () => {
      tokens.removeOnSync(onSyncCb)
      tokens.removeOnError(onErrorCb)
    }
  }, [])

  return { isAuthorized, isAuthReady }
}
